import { useEffect, useState } from "react";

export function useTheme() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Check initial theme
    const checkTheme = () => {
      const htmlElement = document.documentElement;
      setIsDark(htmlElement.classList.contains("dark"));
    };

    // Initial check
    checkTheme();

    // Create observer to watch for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          checkTheme();
        }
      });
    });

    // Start observing
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const getGlassStyles = (isScrolled: boolean) => {
    if (isDark) {
      return {
        backdropFilter: isScrolled
          ? "blur(20px) saturate(1.8)"
          : "blur(12px) saturate(1.5)",
        WebkitBackdropFilter: isScrolled
          ? "blur(20px) saturate(1.8)"
          : "blur(12px) saturate(1.5)",
        background: isScrolled ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.25)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: isScrolled
          ? "0 8px 32px rgba(0, 0, 0, 0.5)"
          : "0 4px 16px rgba(0, 0, 0, 0.3)",
      };
    } else {
      return {
        backdropFilter: isScrolled
          ? "blur(20px) saturate(1.8)"
          : "blur(12px) saturate(1.5)",
        WebkitBackdropFilter: isScrolled
          ? "blur(20px) saturate(1.8)"
          : "blur(12px) saturate(1.5)",
        background: isScrolled
          ? "rgba(255, 255, 255, 0.2)"
          : "rgba(255, 255, 255, 0.1)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: isScrolled
          ? "0 8px 32px rgba(0, 0, 0, 0.1)"
          : "0 4px 16px rgba(0, 0, 0, 0.05)",
      };
    }
  };

  const getControlsGlassStyles = () => {
    if (isDark) {
      return {
        backdropFilter: "blur(10px) saturate(1.2)",
        WebkitBackdropFilter: "blur(10px) saturate(1.2)",
        background: "rgba(0, 0, 0, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
      };
    } else {
      return {
        backdropFilter: "blur(10px) saturate(1.2)",
        WebkitBackdropFilter: "blur(10px) saturate(1.2)",
        background: "rgba(255, 255, 255, 0.15)",
        border: "1px solid rgba(255, 255, 255, 0.3)",
      };
    }
  };

  const getButtonGlassStyles = () => {
    if (isDark) {
      return {
        backdropFilter: "blur(8px) saturate(1.1)",
        WebkitBackdropFilter: "blur(8px) saturate(1.1)",
        background: "rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      };
    } else {
      return {
        backdropFilter: "blur(8px) saturate(1.1)",
        WebkitBackdropFilter: "blur(8px) saturate(1.1)",
        background: "rgba(255, 255, 255, 0.1)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
      };
    }
  };

  const getAvatarGlassStyles = () => {
    if (isDark) {
      return {
        backdropFilter: "blur(10px) saturate(1.2)",
        WebkitBackdropFilter: "blur(10px) saturate(1.2)",
        background: "rgba(0, 0, 0, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
      };
    } else {
      return {
        backdropFilter: "blur(10px) saturate(1.2)",
        WebkitBackdropFilter: "blur(10px) saturate(1.2)",
        background: "rgba(255, 255, 255, 0.15)",
        border: "1px solid rgba(255, 255, 255, 0.25)",
      };
    }
  };

  const getDropdownGlassStyles = () => {
    if (isDark) {
      return {
        backdropFilter: "blur(16px) saturate(1.4)",
        WebkitBackdropFilter: "blur(16px) saturate(1.4)",
        background: "rgba(0, 0, 0, 0.5)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
      };
    } else {
      return {
        backdropFilter: "blur(16px) saturate(1.4)",
        WebkitBackdropFilter: "blur(16px) saturate(1.4)",
        background: "rgba(255, 255, 255, 0.25)",
        border: "1px solid rgba(255, 255, 255, 0.3)",
      };
    }
  };

  return {
    isDark,
    getGlassStyles,
    getControlsGlassStyles,
    getButtonGlassStyles,
    getAvatarGlassStyles,
    getDropdownGlassStyles,
  };
}
